import * as React from "react"

/** Processing components stay available on a public project, so they are not limited. */
export const NON_INPUT_COMPONENT_CATEGORIES = new Set([
  "Outputs",
  "Properties",
  "Conversions",
  "Calculations",
  "Charts",
  "Set operations",
  "Arithmetic",
  "Debug tools",
])

/** Choices stored on a public project. Absent on the regular and embed editors. */
export interface PublicShareSettings {
  mapLayerIds: string[]
  componentIds: string[]
  aegisInstructions: string
  aegisIntro: string
  splash: string
  aegisOptions: string
  /** When false, the public chat omits the default "Explain this map" button. */
  showExplainMap: boolean
  autoRun: boolean
  /** When true, visitors cannot change the area. AEGIS still can. */
  lockExtent: boolean
  agentProvider: string
  agentModel: string
  agentModelLabel: string
  agentConfigured: boolean
  shareCode: string
  /** Extents a visitor can switch the public map to. Empty hides the control. */
  extents: PublicExtentChoice[]
}

/** One area a public viewer can switch the map and model to. */
export interface PublicExtentChoice {
  id: number
  name: string
  value: number[]
  layer?: string | null
  cql?: string | null
  shape?: string | null
  externalLink?: string | null
  level?: number | null
}

export function isPublicExtentChoice(value: unknown): value is PublicExtentChoice {
  if (!value || typeof value !== "object") return false
  const row = value as PublicExtentChoice
  return typeof row.id === "number"
    && typeof row.name === "string"
    && Array.isArray(row.value)
    && row.value.length >= 4
    && row.value.slice(0, 4).every(coord => typeof coord === "number" && Number.isFinite(coord))
}

/** Drop parenthetical notes such as "(village)" from an area name. */
export function extentDisplayName(name: string): string {
  let result = name
  let previous = ""
  while (result !== previous) {
    previous = result
    result = result.replace(/\s*\([^()]*\)/g, "").replace(/\s*\[[^\[\]]*\]/g, "")
  }
  return result.replace(/\s{2,}/g, " ").trim()
}

export function extentChoiceLabel(name: string, level?: number | null): string {
  const depth = typeof level === "number" && level > 0 ? level : 0
  return `${"\u00A0\u00A0".repeat(depth)}${extentDisplayName(name)}`
}

function sameExtentText(left?: string | null, right?: string | null): boolean {
  return (left || "") === (right || "")
}

function normalizedExtent(value: number[]): [number, number, number, number] {
  return [
    Math.min(value[0], value[2]),
    Math.min(value[1], value[3]),
    Math.max(value[0], value[2]),
    Math.max(value[1], value[3]),
  ]
}

export function extentMatchesProject(
  extent: { value?: number[] | null, layer?: string | null, cql?: string | null },
  project: { extent?: number[] | null, layer?: string | null, cql?: string | null }
): boolean {
  if (!project.extent || project.extent.length < 4 || !extent.value || extent.value.length < 4) return false
  const left = normalizedExtent(project.extent)
  const right = normalizedExtent(extent.value)
  if (left.some((coord, index) => Math.abs(coord - right[index]) > 1)) return false
  return sameExtentText(project.layer, extent.layer) && sameExtentText(project.cql, extent.cql)
}

export function projectWithPublicExtent<T extends {
  extent?: number[]
  layer?: string
  cql?: string
  shape?: string
  externalLink?: string
}>(project: T, extent: PublicExtentChoice): T {
  return {
    ...project,
    extent: normalizedExtent(extent.value),
    layer: extent.layer || "",
    cql: extent.cql || "",
    shape: extent.shape || "",
    externalLink: extent.externalLink || "",
  }
}

/** One public quick prompt per line. Blank lines are ignored. */
export function aegisOptionLines(value: string): string[] {
  const seen: { [key: string]: true } = {}
  const options: string[] = []
  value.split(/\r?\n/).forEach(line => {
    const option = line.trim()
    if (!option || seen[option]) return
    seen[option] = true
    options.push(option)
  })
  return options
}

const PublicShareSettingsContext = React.createContext<PublicShareSettings | null>(null)

export function PublicShareSettingsProvider({
  value,
  children,
}: {
  value: PublicShareSettings
  children: React.ReactNode
}) {
  return (
    <PublicShareSettingsContext.Provider value={value}>
      {children}
    </PublicShareSettingsContext.Provider>
  )
}

export function usePublicShareSettings(): PublicShareSettings | null {
  return React.useContext(PublicShareSettingsContext)
}

export function isComponentPubliclyAvailable(
  component: { name: string, category?: string },
  settings: PublicShareSettings | null
): boolean {
  if (!settings) return true
  if (NON_INPUT_COMPONENT_CATEGORIES.has(component.category || "Other")) return true
  return settings.componentIds.includes(`available-${component.name}`)
}

export function isMapLayerPubliclyAvailable(catalogKey: string, settings: PublicShareSettings | null): boolean {
  if (!settings) return true
  return settings.mapLayerIds.includes(`layer-${catalogKey}`)
}
